/**
 * CAM read-visibility STRAWMAN.
 *
 * Mini PRD in-scope: "Captures and accepted actions visible to CAM in the
 * existing document back end. No new CAM tooling in this Mini PRD."
 * Open question #7 asks where exactly captures appear for CAM (Ling input).
 * This screen exists to get a better answer than a blank question - it is
 * NOT a proposed CAM UI.
 */
import { Box, Stack, Typography } from '@mui/material';

import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';
import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { useCaptureFlow } from './CaptureFlowContext';
import { palette } from './components/palette';

export const CamView = () => {
  const { savedCaptures, completedActionKeys, camContactRequested } =
    useCaptureFlow();

  return (
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <Stack spacing={1}>
          <Box
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: palette.softYellow,
              borderRadius: 99,
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            STRAWMAN — team only, for the Scope conversation with Ling
          </Box>
          <PageHeader
            title='CAM view: Olivia Patrick'
            description="What the care manager sees of this carer's captures (read-only in Mini PRD 1; CAM parity is Mini PRD 2)."
          />
        </Stack>
      }
      contentSlot={
        <Stack spacing={1.5}>
          {camContactRequested && (
            <Box
              sx={{
                backgroundColor: palette.lavender,
                borderRadius: 2,
                padding: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 13 }}>
                ⚑ This carer asked to talk to you this session.
              </Typography>
            </Box>
          )}
          {savedCaptures.length === 0 ? (
            <Card isFullWidth>
              <Typography
                sx={{ color: palette.mutedText, fontSize: 13, py: 1 }}
              >
                No captures yet for this carer.
              </Typography>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {savedCaptures.map((capture) => (
                <Card key={capture.id} isFullWidth isPaddingSm>
                  <Stack spacing={1}>
                    <Stack
                      sx={{ alignItems: 'center' }}
                      direction='row'
                      spacing={1.5}
                    >
                      <Icon icon='file-lines' fontSize={18} />
                      <Stack sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 14 }}>
                          {capture.doc.classification}
                        </Typography>
                        <Typography
                          sx={{ color: palette.mutedText, fontSize: 12 }}
                        >
                          {capture.doc.fileName} · via {capture.method} ·{' '}
                          {capture.doc.pageCount} page
                          {capture.doc.pageCount === 1 ? '' : 's'} · saved{' '}
                          {capture.savedAt.toLowerCase()}
                        </Typography>
                      </Stack>
                      {capture.markedWrong && (
                        <Box
                          sx={{
                            backgroundColor: '#fce9e5',
                            color: palette.alertRed,
                            borderRadius: 99,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          Carer flagged wrong
                        </Box>
                      )}
                    </Stack>

                    {capture.acceptedActions.length > 0 ? (
                      <Stack spacing={0.5} sx={{ pl: 4 }}>
                        <Typography
                          sx={{
                            fontSize: 11,

                            color: palette.mutedText,
                            textTransform: 'uppercase',
                          }}
                        >
                          Actions the carer accepted
                        </Typography>
                        {capture.acceptedActions.map((action) => {
                          const done = completedActionKeys.includes(
                            `${capture.id}:${action.id}`,
                          );
                          return (
                            <Typography
                              key={action.id}
                              sx={{ fontSize: 13, color: palette.ink }}
                            >
                              {done ? '✓' : '○'} {action.title}
                              {action.isTweaked && (
                                <Typography
                                  component='span'
                                  sx={{
                                    fontSize: 11,
                                    color: palette.lavenderInk,
                                    ml: 0.5,
                                  }}
                                >
                                  (edited by carer)
                                </Typography>
                              )}
                            </Typography>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography
                        sx={{ fontSize: 12, color: palette.mutedText, pl: 4 }}
                      >
                        Saved without actions.
                      </Typography>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      }
    />
  );
};

export default CamView;
